import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { articles, type Article } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  useScrollToTop();
  const { setMeta } = useDocumentMeta();
  const [liked, setLiked] = useState(false);

  const article = articles.find((a: Article) => a.slug === (slug || ''));
  const relatedArticles = articles
    .filter((a: Article) => a.category === article?.category && a.slug !== (slug || ''))
    .slice(0, 3);

  const imageUrl = article ? (articleImages[article.slug] || articleImages['ai-2026-ren-gong-zhi-neng-zhan-wang']) : '';

  useEffect(() => {
    if (article) {
      setMeta({
        title: article.title + ' - 精品一区',
        description: article.excerpt,
        keywords: '精品一区,' + article.tags.join(',') + ',' + article.categoryName + ',精品内容推荐',
        canonicalUrl: 'https://jingpinyiqu.com/article/' + (slug || ''),
        ogType: 'article',
        ogImage: imageUrl,
        publishedTime: article.date,
        modifiedTime: article.date,
        structuredData: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.date,
              dateModified: article.date,
              image: imageUrl,
              publisher: {
                '@type': 'Organization',
                name: '精品一区',
              },
              url: 'https://jingpinyiqu.com/article/' + (slug || ''),
              articleSection: article.categoryName,
              wordCount: article.content.length,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  item: { '@id': 'https://jingpinyiqu.com', name: '首页' },
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  item: {
                    '@id': 'https://jingpinyiqu.com/category/' + article.category,
                    name: article.categoryName,
                  },
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  item: { '@id': 'https://jingpinyiqu.com/article/' + (slug || ''), name: article.title },
                },
              ],
            },
          ],
        },
      });
    }
  }, [article, slug, setMeta, imageUrl]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: '/category/' + article.category },
              { label: article.title },
            ]}
          />

          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {article.tags.map((tag: string) => (
                <span key={tag} className="inline-block px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8 pb-8 border-b border-background-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                  <div className="text-xs text-foreground-500">资深编辑</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                </span>
              </div>
            </div>

            <div className="relative w-full aspect-[21/9] rounded-lg overflow-hidden bg-background-200 mb-10">
              <img
                src={imageUrl}
                alt={article.title}
                title={article.title + ' - 精品一区精选内容'}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-10
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center justify-between py-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                {article.tags.map((tag: string) => (
                  <span key={tag} className="inline-block px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                    # {tag}
                  </span>
                ))}
              </div>

              <button
                onClick={() => setLiked(!liked)}
                className={
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ' +
                  (liked
                    ? 'bg-accent-100 text-accent-600'
                    : 'bg-background-100 text-foreground-500 hover:text-accent-500')
                }
              >
                <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                {liked ? (article.likeCount + 1).toLocaleString() : article.likeCount.toLocaleString()}
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-16 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-6 h-6 flex items-center justify-center rounded bg-accent-500 text-white">
                  <i className="ri-stack-line text-xs"></i>
                </div>
                <h2 className="text-xl font-heading font-semibold text-foreground-900">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra: Article) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}